import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { OnboardingCard } from '@/components/OnboardingCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Send, Clock, Shield, Mail, ArrowRight, Plus, ArrowDown, CheckCircle, HelpCircle, Gift, Copy, X, Package, Star, RotateCcw, ChevronDown } from 'lucide-react';
import { ReverseSendCard } from '@/components/send/ReverseSendCard';
import ContactsManager from '@/components/send/ContactsManager';
import LiveCountdown from '@/components/send/LiveCountdown';
import { fmtRemaining } from '@/lib/time';

const CARD = 'bg-white border border-slate-200 shadow-sm';
const REF_COLLAPSE_KEY = 'mboa_ref_banner_collapsed';

export default function MboaSendDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [txs, setTxs] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [rate, setRate] = useState(null);
  const [clientFileMissing, setClientFileMissing] = useState(false);
  useEffect(() => { api.get('/user/compliance-profile').then(r => setClientFileMissing(!!(r.data.required && !r.data.complete))).catch(() => {}); }, []);
  const [wallet, setWallet] = useState({ cad: 0, fcfa: 0, mboa_balance: 0 });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [welcomePromo, setWelcomePromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refCopied, setRefCopied] = useState(false);
  const [refCode, setRefCode] = useState('');
  const [referredBy, setReferredBy] = useState(null);
  const [refCollapsed, setRefCollapsed] = useState(() => localStorage.getItem(REF_COLLAPSE_KEY) === '1');

  useEffect(() => {
    Promise.all([
      api.get('/mboasend/transactions').catch(() => ({ data: { transactions: [] } })),
      api.get('/mboasend/wallet-deposits').catch(() => ({ data: { deposits: [] } })),
      api.get('/payouts/my').catch(() => ({ data: { payouts: [] } })),
      api.get('/mboasend/rate').catch(() => ({ data: {} })),
      api.get('/mboasend/wallet').catch(() => ({ data: { cad: 0, fcfa: 0, mboa_balance: 0 } })),
      api.get('/referral/my-code').catch(() => ({ data: {} })),
      api.get('/auth/me').catch(() => ({ data: {} })),
      api.get('/mboasend/beneficiaries').catch(() => ({ data: { beneficiaries: [] } })),
      api.get('/mboasend/first-send-promo').catch(() => ({ data: { eligible: false } })),
    ]).then(([txRes, depRes, payRes, rateRes, walletRes, refRes, meRes, benRes, wpRes]) => {
      setTxs(txRes.data.transactions || []);
      setDeposits(depRes.data.deposits || []);
      setPayouts(payRes.data.payouts || []);
      setRate(rateRes.data);
      setWallet(walletRes.data);
      setRefCode(refRes.data.code || '');
      setReferredBy(meRes.data.referred_by || null);
      setBeneficiaries(benRes.data.beneficiaries || []);
      setWelcomePromo(wpRes.data?.eligible ? wpRes.data : null);
    }).finally(() => setLoading(false));
  }, []);

  const needsKyc = user?.kyc_status !== 'approved';
  const needsEmail = !user?.email_verified;
  const refLink = refCode ? `${window.location.origin}/mboasend/register?ref=${refCode}` : '';

  const toggleRefBanner = () => {
    const next = !refCollapsed;
    setRefCollapsed(next);
    localStorage.setItem(REF_COLLAPSE_KEY, next ? '1' : '0');
  };

  // Navigate to send flow with a prefilled recipient (fixes prefill bug)
  const goSend = (prefill, amount) => {
    if (needsEmail) { toast.error('Vérifiez votre email d\'abord'); return; }
    if (needsKyc) { toast.error('Vérification d\'identité requise'); navigate('/kyc'); return; }
    if ((wallet.cad || 0) <= 0) { toast.error('Rechargez votre compte avant de pouvoir faire un transfert'); return; }
    navigate('/send/new', { state: { prefill, amount } });
  };

  // Filleul promo: was referred and hasn't yet done a qualifying transfer (>=100$)
  const qualifyingSend = [...payouts, ...txs].some(t => {
    const amt = Number(t.amount_cad ?? t.amount_send ?? 0);
    const st = (t.status || '').toLowerCase();
    return amt >= 100 && (st.includes('complet') || st === 'provider_success' || st === 'agent_verified');
  });
  const showReferredPromo = !!referredBy && !qualifyingSend;

  const copyRef = () => {
    if (!refLink) return;
    try { navigator.clipboard?.writeText(refLink); } catch (e) { /* ignore */ }
    setRefCopied(true); setTimeout(() => setRefCopied(false), 2000);
    toast.success('Lien copié !');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#006400] border-t-transparent rounded-full animate-spin" /></div>;

  // Build transaction list
  const allItems = [];
  deposits.forEach(d => allItems.push({ ...d, _type: 'deposit', _date: d.created_at, _label: 'Dépôt Interac', _amount: `+${d.amount || 0} CAD`, _icon: 'down', _status: d.status }));
  payouts.forEach(p => allItems.push({ ...p, _type: 'payout', _date: p.created_at, _label: `${p.recipient_first_name || ''} ${p.recipient_last_name || ''}`.trim() || 'Transfert', _amount: `${p.amount_cad} CAD → ${(p.amount_xaf || 0).toLocaleString('fr-FR')} FCFA`, _icon: 'send', _status: p.status, _prefill: { name: `${p.recipient_first_name || ''} ${p.recipient_last_name || ''}`.trim(), phone: p.recipient_phone || '', operator: p.recipient_network || 'mtn' }, _resendAmount: p.amount_cad }));
  txs.forEach(t => allItems.push({ ...t, _type: 'transfer', _date: t.created_at, _label: t.beneficiary_name || 'Transfert', _amount: `${t.amount_send} CAD → ${Number(t.amount_receive).toLocaleString('fr-FR')} FCFA`, _icon: 'send', _status: t.status, _prefill: { name: t.beneficiary_name || '', phone: t.beneficiary_phone || '', operator: t.beneficiary_operator || t.recipient_network || 'mtn' }, _resendAmount: t.amount_send }));
  allItems.sort((a, b) => (b._date || '').localeCompare(a._date || ''));

  const statusLabel = (st) => {
    const s = (st || '').toLowerCase();
    if (s.includes('complet') || s === 'completed' || s === 'provider_success') return 'Complété';
    if (s.includes('fail') || s.includes('reject')) return 'Échoué';
    if (s.includes('refund')) return 'Remboursé';
    return 'En cours';
  };
  const statusColor = (st) => {
    const s = (st || '').toLowerCase();
    if (s.includes('complet') || s === 'completed') return 'bg-emerald-100 text-[#006400]';
    if (s.includes('fail') || s.includes('reject')) return 'bg-red-100 text-red-700';
    return 'bg-[#FFDA00]/25 text-[#8a6d00]';
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="mboasend-dashboard">
      {/* Alerts */}
      {needsEmail && (
        <div className={`${CARD} rounded-xl p-3 flex items-center gap-3 border-[#FFDA00]/60`}>
          <Mail className="w-5 h-5 text-[#B8860B] flex-shrink-0" />
          <p className="text-xs text-[#8a6d00] flex-1">Vérifiez votre email pour sécuriser votre compte</p>
          <Button size="sm" variant="outline" className="border-[#FFDA00]/60 text-[#8a6d00] text-[10px] h-7"
            onClick={async () => { try { await api.post('/auth/resend-verification', { email: user.email }); toast.success('Email envoyé !'); } catch { toast.error('Erreur'); } }}>
            Renvoyer
          </Button>
        </div>
      )}
      {!needsEmail && <OnboardingCard />}

      {/* Welcome rate badge — active while the first-transfer offer is valid */}
      {welcomePromo?.eligible && (
        <div className="bg-gradient-to-r from-[#006400] to-[#00843D] rounded-xl p-3.5 flex items-center gap-3 shadow-sm" data-testid="welcome-rate-badge">
          <div className="w-10 h-10 rounded-full bg-[#FFDA00]/90 flex items-center justify-center flex-shrink-0">
            <Star className="w-5 h-5 text-[#0D2612] fill-[#0D2612]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Bonus de bienvenue : +{String(welcomePromo.bonus_per_cad).replace('.', ',')} FCFA par dollar</p>
            <p className="text-[10px] text-white/85">
              Appliqué automatiquement à votre 1er transfert, en plus du taux du jour.
              {fmtRemaining(welcomePromo.deadline) && <span className="text-[#FFDA00] font-medium"> ⏳ Plus que <LiveCountdown deadline={welcomePromo.deadline} /></span>}
            </p>
          </div>
          <Button size="sm" onClick={() => goSend(null)} className="bg-[#FFDA00] text-[#0D2612] hover:bg-[#E6C400] h-8 text-xs flex-shrink-0 font-semibold" data-testid="welcome-rate-send-btn">
            Envoyer
          </Button>
        </div>
      )}

      {/* Filleul promo — referred user who hasn't made a qualifying transfer yet */}
      {showReferredPromo && (
        <div className="bg-[#FFDA00]/15 border border-[#FFDA00]/60 rounded-xl p-3 flex items-center gap-3" data-testid="referred-promo">
          <div className="w-9 h-9 rounded-lg bg-[#FFDA00]/40 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-[#B8860B]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#8a6d00]">Faites un premier envoi de 100$ ou plus</p>
            <p className="text-[10px] text-slate-600">et recevez <b>10$ de crédit</b> — vous <b>et</b> votre parrain !</p>
          </div>
        </div>
      )}

      {/* Balances */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`${CARD} rounded-xl p-4 relative`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">🇨🇦</span>
              <span className="text-[10px] text-slate-500 uppercase">Solde CAD</span>
            </div>
            <button onClick={() => navigate('/send/deposit')} className="w-6 h-6 rounded-full bg-[#006400]/10 flex items-center justify-center hover:bg-[#006400]/20">
              <Plus className="w-3.5 h-3.5 text-[#006400]" />
            </button>
          </div>
          <p className="text-2xl font-bold text-[#0D2612]" style={{ fontFamily: 'JetBrains Mono' }}>
            {wallet.cad?.toFixed(2)}<span className="text-sm text-slate-500 ml-1">$</span>
          </p>
          {rate && <p className="text-[9px] text-slate-400 mt-1">1$ = {rate.rate_ca_cm} FCFA{rate.best_rate_ca_cm > rate.rate_ca_cm ? ` · jusqu’à ${String(rate.best_rate_ca_cm).replace('.', ',')} dès ${rate.rate_tiers?.[rate.rate_tiers.length - 1]?.above_cad} $` : ''}</p>}
        </div>
        <div className={`${CARD} rounded-xl p-4 border-[#FFDA00]/40`}>
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-[#B8860B]" />
            <span className="text-[10px] text-slate-500 uppercase">Récompenses</span>
          </div>
          <p className="text-2xl font-bold text-[#B8860B]" style={{ fontFamily: 'JetBrains Mono' }}>
            {(wallet.mboa_balance || 0).toLocaleString()}<span className="text-sm text-slate-500 ml-1">MBOA</span>
          </p>
          <p className="text-[9px] text-slate-400 mt-1">1 MBOA = 1 FCFA</p>
        </div>
      </div>

      {/* Referral banner — collapsible to reduce clutter */}
      {refCollapsed ? (
        <button onClick={toggleRefBanner} className={`${CARD} rounded-xl p-3 w-full flex items-center gap-3 border-[#006400]/20`} data-testid="referral-banner-collapsed">
          <div className="w-8 h-8 rounded-lg bg-[#006400]/10 flex items-center justify-center flex-shrink-0">
            <Gift className="w-4 h-4 text-[#006400]" />
          </div>
          <p className="text-xs font-medium text-[#006400] flex-1 text-left">Parrainez un ami, gagnez 10$ chacun</p>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
      ) : (
      <div className={`${CARD} rounded-xl p-4 space-y-3 border-[#006400]/20`} data-testid="referral-banner">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#006400]/10 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-[#006400]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#006400]">Parrainez un ami, gagnez 10$ chacun</p>
            <p className="text-[10px] text-slate-500">Vous et votre filleul recevez 10$ dès son 1er transfert de 100$+</p>
          </div>
          <button onClick={toggleRefBanner} className="text-slate-400 hover:text-slate-600 flex-shrink-0" aria-label="Réduire" data-testid="referral-banner-collapse"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[#F5F4EF] rounded-lg px-3 py-2">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider">Votre code</p>
            <p className="text-base font-bold text-[#B8860B] font-mono" data-testid="referral-code">{refCode || '—'}</p>
          </div>
          <Button size="sm" onClick={() => { if (refCode) { try { navigator.clipboard?.writeText(refCode); } catch (e) {} toast.success('Code copié !'); } }}
            className="bg-[#FFDA00] text-[#0D2612] hover:bg-[#E6C400] h-11 text-xs flex-shrink-0" data-testid="copy-referral-code">
            Copier le code
          </Button>
        </div>
        <div className="flex items-center gap-2 bg-[#F5F4EF] rounded-lg px-3 py-2 cursor-pointer" onClick={copyRef} data-testid="referral-link-box">
          <p className="text-[10px] text-slate-600 truncate flex-1" data-testid="referral-link">{refLink || 'Chargement...'}</p>
          <span className="text-[#006400] flex-shrink-0">
            {refCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </span>
        </div>
        <Link to="/send/referral" className="block text-center text-[11px] text-[#006400] hover:underline" data-testid="referral-details-link">
          Voir mes gains de parrainage →
        </Link>
      </div>
      )}

      {/* CANAFE client file missing (clients registered before it became mandatory) */}
      {clientFileMissing && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="client-file-banner">
          <p className="text-xs text-red-800">Avant votre prochain envoi, complétez vos informations personnelles (1 minute, une seule fois).</p>
          <button onClick={() => navigate('/profile')} className="text-xs font-semibold text-white bg-[#006400] rounded-lg px-3 py-2 flex-shrink-0">Compléter</button>
        </div>
      )}

      {/* Main actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={() => {
          if (needsEmail) { toast.error('Vérifiez votre email d\'abord'); return; }
          if (needsKyc) { toast.error('Vérification d\'identité requise'); navigate('/kyc'); return; }
          if (wallet.cad <= 0) { toast.error('Rechargez votre compte avant de pouvoir faire un transfert'); return; }
          navigate('/send/new');
        }}
          className={`h-14 rounded-xl font-bold text-base flex flex-col items-center gap-1 ${needsKyc || needsEmail || wallet.cad <= 0 ? 'bg-slate-200 text-slate-400' : 'bg-[#006400] text-white hover:bg-[#004D00] shadow-md'}`}
          data-testid="send-money-btn">
          <Send className="w-5 h-5" />
          <span className="text-xs">Envoyer</span>
        </Button>
        <Button onClick={() => navigate('/send/deposit')}
          className="h-14 rounded-xl font-bold text-base flex flex-col items-center gap-1 bg-white text-[#0D2612] border border-slate-200 hover:border-[#006400]/50"
          data-testid="add-money-btn">
          <Plus className="w-5 h-5 text-[#006400]" />
          <span className="text-xs">Recharger</span>
        </Button>
      </div>

      {/* Zero balance message */}
      {!needsKyc && !needsEmail && wallet.cad <= 0 && (
        <div className="bg-[#FFDA00]/10 border border-[#FFDA00]/50 rounded-xl p-3 text-center">
          <p className="text-xs text-[#8a6d00]">Vous devez recharger votre compte avant de pouvoir faire un transfert.</p>
          <button onClick={() => navigate('/send/deposit')} className="text-[10px] text-[#006400] hover:underline mt-1">Cliquez ici pour commencer →</button>
        </div>
      )}

      {/* Quick send — derniers destinataires (avatars) */}
      {beneficiaries.length > 0 && !needsKyc && !needsEmail && (
        <div data-testid="quick-recipients">
          <h2 className="text-sm font-semibold mb-2 text-[#0D2612]" style={{ fontFamily: 'Outfit' }}>Envoyer à</h2>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {beneficiaries.slice(0, 8).map((b) => (
              <button key={b.id} onClick={() => goSend({ name: b.name, phone: b.phone, operator: b.operator || 'mtn' })}
                className={`relative flex-shrink-0 ${CARD} rounded-xl p-3 flex flex-col items-center gap-1.5 min-w-[80px] hover:border-[#006400]/40 transition-colors`}
                data-testid={`quick-recipient-${b.id}`}>
                {b.favorite && <Star className="w-3 h-3 text-[#B8860B] fill-[#B8860B] absolute top-1.5 right-1.5" />}
                <div className="w-9 h-9 rounded-full bg-[#006400]/10 flex items-center justify-center">
                  <span className="text-[#006400] text-xs font-bold">{b.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                </div>
                <span className="text-[10px] text-[#0D2612] truncate w-full text-center">{b.name.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contacts management */}
      {!needsKyc && !needsEmail && (
        <ContactsManager beneficiaries={beneficiaries} setBeneficiaries={setBeneficiaries}
          onSend={(b) => goSend({ name: b.name, phone: b.phone, operator: b.operator || 'mtn' })} />
      )}

      {/* Recent transactions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#0D2612]" style={{ fontFamily: 'Outfit' }}>Transactions récentes</h2>
          <Link to="/send/history" className="text-[10px] text-[#006400] hover:underline">Voir tout</Link>
        </div>
        {allItems.length === 0 ? (
          <div className={`${CARD} rounded-xl p-8 text-center`}>
            <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Aucune transaction</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allItems.slice(0, 6).map((item, i) => (
              <div key={item.id || i} className={`${CARD} rounded-xl p-3 flex items-center justify-between`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item._icon === 'down' ? 'bg-[#006400]/10' : 'bg-[#FFDA00]/25'}`}>
                    {item._icon === 'down' ? <ArrowDown className="w-4 h-4 text-[#006400]" /> : <Send className="w-4 h-4 text-[#B8860B]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate text-[#0D2612]">{item._label}</p>
                    <p className="text-[9px] text-slate-400">{new Date(item._date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-xs font-bold text-[#0D2612]">{item._amount}</p>
                  <Badge className={`${statusColor(item._status)} border-0 text-[8px]`}>{statusLabel(item._status)}</Badge>
                  {item._prefill?.phone && !needsKyc && !needsEmail && (
                    <button onClick={() => goSend(item._prefill, item._resendAmount)}
                      className="mt-1 ml-auto flex items-center gap-1 text-[9px] text-[#006400] hover:underline"
                      data-testid={`resend-${item.id || i}`}>
                      <RotateCcw className="w-2.5 h-2.5" /> Renvoyer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Services */}
      <div>
        <h2 className="text-sm font-semibold mb-3 text-[#0D2612]" style={{ fontFamily: 'Outfit' }}>Services</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className={`${CARD} rounded-xl p-4 flex flex-col items-center gap-2 opacity-50 cursor-not-allowed`}>
            <Package className="w-6 h-6 text-slate-400" />
            <span className="text-xs text-slate-500">SendColis</span>
            <Badge className="bg-slate-100 text-slate-500 border-0 text-[8px]">Bientôt</Badge>
          </div>
          <div className={`${CARD} rounded-xl p-4 flex flex-col items-center gap-2 opacity-50 cursor-not-allowed`}>
            <Star className="w-6 h-6 text-slate-400" />
            <span className="text-xs text-slate-500">MboaPay</span>
            <Badge className="bg-slate-100 text-slate-500 border-0 text-[8px]">Bientôt</Badge>
          </div>
        </div>
      </div>

      {/* Cameroun → Canada (concierge via WhatsApp) */}
      <ReverseSendCard />

      {/* Legal links */}
      <div className="border-t border-slate-200 pt-4">
        <div className="flex flex-wrap justify-center gap-2 text-[9px] text-slate-400">
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Confidentialité</a>
          <span>|</span>
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">CGU</a>
          <span>|</span>
          <a href="/aml-kyc-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">AML/KYC</a>
          <span>|</span>
          <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Remboursement</a>
          <span>|</span>
          <a href="/legal-notice" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Mentions légales</a>
        </div>
      </div>
    </div>
  );
}
